import { useMemo, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { ChevronRight, FolderClosed, Inbox, Search, Users } from "lucide-react";

import { FolderDropBar } from "@app/components/FolderShelf";
import { Checkbox } from "@app/components/ui/checkbox";
import { Input } from "@app/components/ui/input";
import { Switch } from "@app/components/ui/switch";
import { cn } from "@app/lib/utils";
import type { GroupingRequest, StudyVariable } from "@app/types/study";
import { UNFILED, type CohortFolder } from "@app/utils/cohort-folders";
import { useFolderDrop } from "@app/utils/use-folder-drop";

import { MAX_STUDY_COHORTS as MAX_COHORTS } from "./state";
import { VariablePicker } from "./VariablePicker";

export interface CatalogueCohort {
  id: string;
  name: string;
  description?: string | null;
  /** The reader's classeur it is filed in (PT-162); null for « Sans classeur ». */
  folder_id?: string | null;
  /** A colleague's cohort under the anonymity threshold has its size withheld (`null`). */
  metadata?: { patient_count?: number | null; episode_count?: number | null; specialite?: string } | null;
}

const GROUPABLE = new Set(["categorical", "binary", "ordinal", "multi", "date", "numeric"]);

interface Props {
  cohorts: CatalogueCohort[];
  /** The reader's classeurs: the cohorts are listed under them, then « Sans classeur ». */
  folders: CohortFolder[];
  /** Files a cohort dropped on a classeur; absent where the session may not file. */
  onFile?: (cohortId: string, folderId: string | null) => void;
  selected: string[];
  onSelected: (ids: string[]) => void;
  grouping: GroupingRequest;
  onGrouping: (grouping: GroupingRequest) => void;
  variables: StudyVariable[];
}

/** Who is studied, and how they are split into groups. */
export function PopulationPanel({
  cohorts,
  folders,
  onFile,
  selected,
  onSelected,
  grouping,
  onGrouping,
  variables,
}: Props) {
  const { t } = useLingui();
  const [filter, setFilter] = useState("");
  const filtering = filter.trim() !== "";
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? cohorts.filter((c) => c.name.toLowerCase().includes(q)) : cohorts;
  }, [cohorts, filter]);
  // Classeurs open in the list; every one is while searching.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const folderIds = useMemo(() => new Set(folders.map((folder) => folder.id)), [folders]);
  // A cohort filed in a classeur that did not load is « Sans classeur » here.
  const folderOf = (cohort: CatalogueCohort) =>
    cohort.folder_id && folderIds.has(cohort.folder_id) ? cohort.folder_id : null;
  const drop = useFolderDrop<CatalogueCohort>({
    canDrop: (cohort, target) => target !== (folderOf(cohort) ?? UNFILED),
    onDrop: (cohort, target) => onFile?.(cohort.id, target === UNFILED ? null : target),
  });
  const toggleFolder = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const grouper = grouping.kind === "variable" ? variables.find((v) => v.key === grouping.key) : undefined;

  const toggleCohort = (id: string) => {
    const next = selected.includes(id)
      ? selected.filter((c) => c !== id)
      : selected.length >= MAX_COHORTS
        ? selected
        : [...selected, id];
    onSelected(next);
    if (next.length > 1) onGrouping({ kind: "cohorts" });
    else if (grouping.kind === "cohorts") onGrouping({ kind: "none" });
  };

  const unfiled = shown.filter((cohort) => folderOf(cohort) === null);
  // Classeurs are created in the catalogue: without one, a row has nowhere to go.
  const dragsToFolders = Boolean(onFile) && folders.length > 0;

  const renderCohort = (cohort: CatalogueCohort) => {
    const isSelected = selected.includes(cohort.id);
    return (
      <label
        key={cohort.id}
        draggable={dragsToFolders}
        onDragStart={dragsToFolders ? (event) => drop.startDrag(cohort, event) : undefined}
        onDragEnd={drop.endDrag}
        className={cn(
          "flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition-colors",
          isSelected ? "border-primary/40 bg-primary/5" : "border-slate-200 hover:border-primary/50",
          drop.dragged?.id === cohort.id && "opacity-50",
        )}
      >
        <Checkbox checked={isSelected} onCheckedChange={() => toggleCohort(cohort.id)} className="mt-0.5" />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-slate-900">{cohort.name}</div>
          {cohort.metadata?.patient_count != null && (
            <div className="text-2xs text-slate-400">
              <Trans>
                {cohort.metadata.patient_count} patients · {cohort.metadata.episode_count ?? "?"} épisodes
              </Trans>
            </div>
          )}
        </div>
      </label>
    );
  };

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-900">
          <Trans>Population</Trans>
        </h2>
        <p className="text-xs text-slate-500">
          <Trans>Une ou plusieurs cohortes du catalogue ({MAX_COHORTS} au plus).</Trans>
        </p>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t`Rechercher une cohorte...`}
            className="h-9 pl-9 text-sm"
          />
        </div>
        <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
          {folders.length === 0 ? (
            shown.map(renderCohort)
          ) : (
            <>
              {folders.map((folder) => {
                const members = shown.filter((cohort) => folderOf(cohort) === folder.id);
                // Empty classeurs stay listed as drop targets, except in a search's results.
                if (filtering && members.length === 0) return null;
                const open = filtering || expanded.has(folder.id);
                const picked = members.filter((cohort) => selected.includes(cohort.id)).length;
                return (
                  <div
                    key={folder.id}
                    {...drop.dropZone(folder.id)}
                    className={cn("rounded-lg border-2 border-transparent", drop.dropStyle(folder.id))}
                  >
                    <button
                      type="button"
                      onClick={() => toggleFolder(folder.id)}
                      aria-expanded={open}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-primary/5"
                    >
                      <ChevronRight
                        className={cn("h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform", open && "rotate-90")}
                      />
                      <FolderClosed className="h-4 w-4 shrink-0 text-primary" />
                      <span className="min-w-0 flex-1 truncate font-medium">{folder.name}</span>
                      {picked > 0 && (
                        <span
                          className="rounded-full bg-primary/10 px-1.5 text-2xs font-medium text-primary"
                          title={t`Cohortes sélectionnées dans ce classeur`}
                        >
                          {picked}
                        </span>
                      )}
                      <span className="text-2xs text-slate-400">{members.length}</span>
                    </button>
                    {open && members.length > 0 && (
                      <div className="ml-3 mt-1 space-y-1 border-l border-slate-200 pl-2">
                        {members.map(renderCohort)}
                      </div>
                    )}
                  </div>
                );
              })}
              {!(filtering && unfiled.length === 0) && (
                <div
                  {...drop.dropZone(UNFILED)}
                  className={cn("rounded-lg border-2 border-transparent", drop.dropStyle(UNFILED))}
                >
                  <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Inbox className="h-3.5 w-3.5" />
                    <Trans>Sans classeur</Trans>
                  </div>
                  <div className="space-y-1">{unfiled.map(renderCohort)}</div>
                </div>
              )}
            </>
          )}
          {shown.length === 0 && (
            <p className="py-4 text-center text-xs text-slate-400">
              <Trans>Aucune cohorte. Créez-en une depuis le tableau de bord.</Trans>
            </p>
          )}
        </div>
      </section>

      {selected.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-900">
            <Trans>Groupes</Trans>
          </h2>
          {selected.length > 1 ? (
            <p className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <Users className="h-3.5 w-3.5 text-primary" />
              <Trans>Chaque cohorte forme un groupe.</Trans>
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs">
                {(["none", "variable"] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => onGrouping(kind === "none" ? { kind: "none" } : { kind: "variable", key: null })}
                    className={cn(
                      "flex-1 rounded-md px-2 py-1.5 transition-colors",
                      grouping.kind === kind ? "bg-primary text-white" : "text-slate-600 hover:bg-primary/5",
                    )}
                  >
                    {kind === "none" ? t`Décrire la cohorte` : t`Comparer selon…`}
                  </button>
                ))}
              </div>
              {grouping.kind === "variable" && (
                <div className="space-y-2">
                  <VariablePicker
                    mode="single"
                    variables={variables}
                    allow={(v) => GROUPABLE.has(v.type)}
                    value={grouping.key ?? null}
                    onChange={(key) =>
                      onGrouping({
                        kind: "variable",
                        key,
                        values: [],
                        rest: false,
                        threshold: null,
                      })
                    }
                    placeholder={t`Choisir la variable qui fait les groupes`}
                    className="w-full"
                  />
                  {grouper && grouper.type === "numeric" && (
                    <label className="block space-y-1">
                      <span className="text-xs text-slate-500">
                        <Trans>Seuil (deux groupes : sous le seuil, puis au-dessus ou égal)</Trans>
                      </span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={grouping.threshold ?? ""}
                        onChange={(e) =>
                          onGrouping({
                            ...grouping,
                            threshold: e.target.value === "" ? null : Number(e.target.value),
                          })
                        }
                        placeholder={t`Ex. : 30`}
                        className="h-9"
                      />
                    </label>
                  )}
                  {grouper && grouper.type !== "numeric" && (
                    <div className="space-y-2">
                      <span className="text-xs text-slate-500">
                        <Trans>Valeurs comparées (aucune choisie : les plus fréquentes)</Trans>
                      </span>
                      <div className="flex max-h-48 flex-wrap gap-1.5 overflow-y-auto">
                        {grouper.levels.slice(0, 40).map((level) => {
                          const active = (grouping.values ?? []).includes(level.value);
                          return (
                            <button
                              key={level.value}
                              type="button"
                              onClick={() =>
                                onGrouping({
                                  ...grouping,
                                  values: active
                                    ? (grouping.values ?? []).filter((v) => v !== level.value)
                                    : [...(grouping.values ?? []), level.value],
                                })
                              }
                              className={cn(
                                "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                                active
                                  ? "border-primary bg-primary text-white"
                                  : "border-slate-200 text-slate-600 hover:border-primary/50",
                              )}
                            >
                              {level.label}{" "}
                              <span className={active ? "text-white/70" : "text-slate-400"}>{level.n}</span>
                            </button>
                          );
                        })}
                      </div>
                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        <Switch
                          checked={grouping.rest ?? false}
                          onCheckedChange={(rest) => onGrouping({ ...grouping, rest })}
                        />
                        <Trans>Ajouter un groupe « autres valeurs »</Trans>
                      </label>
                      {grouper.levels_withheld && (
                        <p className="text-2xs text-slate-400">
                          <Trans>Les valeurs portées par trop peu de patients ne sont pas proposées (anonymat).</Trans>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {drop.dragged && (
        <FolderDropBar
          folders={folders}
          unfiled={UNFILED}
          drop={drop}
          emptyText={<Trans>Aucun classeur : créez-en un d'abord.</Trans>}
        />
      )}
    </div>
  );
}
